import {CustomItem, Tabs} from '@jetbrains/ring-ui/components/tabs/tabs'
import * as React from 'react'
import {Suspense, useCallback} from 'react'
import {EntryPointContainer} from 'react-relay'
import {useLocation, useNavigate} from 'react-router-dom'

import {useIsOverviewLinkSelected, useQueryParam} from '../../../hooks/routes'
import {usePermission} from '../../../hooks/usePermission'
import {useTabs} from '../../../hooks/useTabs'
import {getHrefWithQueryParams} from '../../../routes'
import {calcNextTab, getTabUrl} from '../../../selectors'
import type {Tab, TabId} from '../../../types'
import {
  Permission,
  ProjectPageTabNamesEnum,
  ProjectPageTabTitlesEnum,
  stringifyId,
} from '../../../types'
import {internalProps} from '../../../types/BS_types'
import {stringifyBranch} from '../../../utils/branchNames'
import {emptyArray} from '../../../utils/empty'
import {parseTabTitle} from '../../../utils/tabs'
import ClassicUITabAdapter from '../../common/ClassicUITabAdapter/ClassicUITabAdapter'
import Loader from '../../common/Loader/Loader'
import {useMode} from '../../common/ModeSwitch/ModeSwitch'
import OverviewTab, {
  createClassicUIOverviewTab,
  renderTitle,
} from '../../common/OverviewTab/OverviewTab'
import tabStyles from '../../common/OverviewTab/OverviewTab.css'
import MammothHint from '../../common/OverviewTab/OverviewTab.mammoth'
import TabPlugin from '../../common/Plugin/TabPlugin/TabPlugin'
import TabsLoader from '../../common/TabsLoader/TabsLoader'

import ProjectChangeLogTab from './ProjectChangeLogTab/ProjectChangeLogTab'
import {Modes} from './ProjectOverviewTab/NestedProjectTrendsOrBuildsOverview.modes'
import ProjectOverviewTab from './ProjectOverviewTab/ProjectOverviewTab'
import {EditPermissionContext} from './ProjectPage.context'
import {useRefetchProject} from './ProjectPage.hooks'
import type {Props} from './ProjectPage.types'
import ProjectPageHeader from './ProjectPageHeader/ProjectPageHeader'
import ProjectProblemsTab from './ProjectProblemsTab/ProjectProblemsTab'

import styles from './ProjectPage.css'

import {useAppSelector} from 'src/hooks/react-redux'

function ProjectPage(props: Props) {
  const {projectId, projectLoaded, branch, projectKey, investigationsTabEntryPoint} = props
  useRefetchProject(projectId, projectKey)
  const mode = useMode()
  const isOverview = useIsOverviewLinkSelected()

  const hasEditPermission = usePermission(Permission.EDIT_PROJECT, projectId)
  const location = useLocation()
  const navigate = useNavigate()
  const currentTabId = useQueryParam('projectTab')
  const currentTab = useAppSelector(state => calcNextTab(state, {tab: currentTabId, projectId}))
  const {
    tabs: {sakuraTabs, classicTabs},
    allTabsReady,
  } = useTabs(props)

  const onSelect = useCallback(
    (tab: TabId) => {
      navigate(
        getHrefWithQueryParams(location, null, params => ({
          projectTab: tab,
          branch: params.branch,
        })),
      )
    },
    [location, navigate],
  )
  const getImplementedInSakuraUITabs = React.useCallback(
    (id: TabId) => {
      switch (id) {
        case ProjectPageTabNamesEnum.OVERVIEW:
          return <ProjectOverviewTab projectId={projectId} />
        case ProjectPageTabNamesEnum.INVESTIGATIONS:
          return internalProps['teamcity.ui.sakuraInvestigationsTab'] &&
            investigationsTabEntryPoint != null ? (
            <EntryPointContainer
              entryPointReference={investigationsTabEntryPoint}
              props={{projectId}}
            />
          ) : null
        case ProjectPageTabNamesEnum.CHANGE_LOG:
          return <ProjectChangeLogTab projectId={projectId} />
        case ProjectPageTabNamesEnum.PROBLEMS:
          return internalProps['teamcity.ui.sakuraProblemsTab'] ? (
            <ProjectProblemsTab projectId={projectId} />
          ) : null
        default:
          return null
      }
    },
    [projectId, investigationsTabEntryPoint],
  )

  const renderTab = React.useCallback(
    (tab?: Tab) => {
      let tabId = currentTab
      let titleText =
        ProjectPageTabTitlesEnum[tabId] ??
        stringifyId(tabId).charAt(0).toUpperCase() + stringifyId(tabId).slice(1)
      let title = renderTitle(titleText)

      if (tab != null) {
        const {name, counter} = parseTabTitle(tab.title)
        tabId = tab.id
        titleText = internalProps['teamcity.ui.sakuraProblemsTab']
          ? ProjectPageTabTitlesEnum[tabId] ?? name
          : name
        title = renderTitle(titleText, counter)
      }

      if (tab?.onlyClassicUI === true) {
        return createClassicUIOverviewTab({id: tabId, title: titleText, href: tab.href})
      } else {
        const tabContent = getImplementedInSakuraUITabs(tabId)
        const Wrapper = tab?.sakura === true ? TabPlugin : ClassicUITabAdapter

        return (
          <OverviewTab
            key={stringifyId(tabId)}
            id={tabId}
            title={title}
            titleProps={{'aria-label': titleText}}
            isClassic={tabContent == null}
            href={getTabUrl({projectId}, tabId)}
            unlimitedWidth={tabId === ProjectPageTabNamesEnum.OVERVIEW && mode === Modes.TRENDS}
          >
            <Suspense fallback={<Loader />}>
              {tabContent || (
                <Wrapper
                  key={stringifyBranch(branch)}
                  tabId={tabId}
                  title={titleText}
                  projectId={projectId}
                  branch={branch}
                  js={tab?.js}
                  css={tab?.css}
                  url={tab?.url}
                />
              )}
            </Suspense>
          </OverviewTab>
        )
      }
    },
    [branch, currentTab, getImplementedInSakuraUITabs, mode, projectId],
  )

  const classicUITabs = React.useMemo(
    () => (classicTabs.length ? classicTabs.map(renderTab) : emptyArray),
    [renderTab, classicTabs],
  )
  const sakuraUITabs = React.useMemo(
    () => (sakuraTabs.length ? sakuraTabs.map(renderTab) : [renderTab()]),
    [sakuraTabs, renderTab],
  )

  if (!projectLoaded) {
    return <Loader className={styles.loader} />
  }

  return (
    <EditPermissionContext.Provider value={hasEditPermission}>
      <ProjectPageHeader projectId={projectId} />
      {isOverview ? (
        <ProjectOverviewTab projectId={projectId} />
      ) : (
        <Tabs
          className={tabStyles.tabs}
          data-test="overview-tabs-panel"
          selected={currentTab}
          onSelect={onSelect}
          initialVisibleItems={3}
          autoCollapse={process.env.NODE_ENV !== 'test'}
          morePopupBeforeEnd={
            (classicTabs.some(tab => tab.onlyClassicUI) && <MammothHint />) || null
          }
          morePopupItemClassName={styles.morePopupItemClassName}
        >
          {sakuraUITabs}
          {classicUITabs}
          {!allTabsReady && (
            <CustomItem key="tabs_loader">
              <TabsLoader />
            </CustomItem>
          )}
        </Tabs>
      )}
    </EditPermissionContext.Provider>
  )
}

export default React.memo(ProjectPage)
